/** Keep editor overlays inside their modal owner, outside nested scroll clips. */
export function composerOverlayOwner(anchor: HTMLElement): HTMLElement {
  return (
    anchor.closest<HTMLElement>('[role="dialog"], [role="alertdialog"]') ??
    anchor.ownerDocument.body
  );
}

export function composerOverlayPositioning(anchor: HTMLElement) {
  const appendTo = composerOverlayOwner(anchor);
  // Popper 2 does not detect individual CSS translate as a fixed containing
  // block. Absolute positioning uses the dialog's native offsetParent instead.
  const strategy: 'fixed' | 'absolute' =
    appendTo === anchor.ownerDocument.body ? 'fixed' : 'absolute';
  return { appendTo, popperOptions: { strategy } };
}

export function composerOverlayAnchorIsVisible(anchor: HTMLElement) {
  const visibility =
    anchor.ownerDocument.defaultView?.getComputedStyle(anchor).visibility;
  return (
    anchor.isConnected &&
    !anchor.closest('[hidden], [inert], [aria-hidden="true"]') &&
    visibility !== 'hidden' &&
    visibility !== 'collapse' &&
    anchor.getClientRects().length > 0
  );
}

/** Only observe the mounted anchor chain; canvas descendants are unrelated. */
export function observeComposerOverlayAnchor(
  anchor: HTMLElement,
  update: () => void,
  overlay?: HTMLElement
) {
  const view = anchor.ownerDocument.defaultView;
  if (!view) return () => {};
  let active = true;
  let frame: number | null = null;
  const schedule = () => {
    if (!active || frame !== null) return;
    frame = view.requestAnimationFrame(() => {
      frame = null;
      if (active) update();
    });
  };
  const resize =
    typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(schedule);
  const mutations = new MutationObserver(schedule);
  for (
    let element: HTMLElement | null = anchor;
    element;
    element = element.parentElement
  ) {
    resize?.observe(element);
    mutations.observe(element, {
      attributes: true,
      attributeFilter: ['class', 'style', 'hidden', 'inert', 'aria-hidden'],
      childList: true,
    });
  }
  if (overlay) resize?.observe(overlay);
  view.addEventListener('scroll', schedule, true);
  view.addEventListener('resize', schedule);
  view.visualViewport?.addEventListener('scroll', schedule);
  view.visualViewport?.addEventListener('resize', schedule);
  schedule();
  return () => {
    active = false;
    if (frame !== null) view.cancelAnimationFrame(frame);
    resize?.disconnect();
    mutations.disconnect();
    view.removeEventListener('scroll', schedule, true);
    view.removeEventListener('resize', schedule);
    view.visualViewport?.removeEventListener('scroll', schedule);
    view.visualViewport?.removeEventListener('resize', schedule);
  };
}

/**
 * The composer card's elevation: a tokenized hairline, a faint top highlight
 * and a soft layered drop. Overlays anchored to the composer (the @ picker)
 * use the same value so they sit at the composer's height, not above it.
 * Needs `--org-chat-composer-border` in scope (`org-chat-composer-outline`).
 */
export const CHAT_COMPOSER_ELEVATION_CLASS_NAME =
  'shadow-[inset_0_0_0_1px_var(--org-chat-composer-border),inset_0_1px_0_rgba(255,255,255,0.7),0_1px_1px_rgba(0,0,0,0.03),0_4px_10px_-3px_rgba(0,0,0,0.06),0_16px_36px_-14px_rgba(0,0,0,0.14)] dark:shadow-[inset_0_0_0_1px_var(--org-chat-composer-border),inset_0_1px_0_rgba(255,255,255,0.05),0_1px_1px_rgba(0,0,0,0.2),0_8px_20px_-6px_rgba(0,0,0,0.35),0_20px_44px_-16px_rgba(0,0,0,0.5)]';

/**
 * The shared popover elevation, `--popover-shadow`, for reference hover
 * cards that open from composer chips, like every other menu in the app.
 */
export const CHAT_COMPOSER_MENU_ELEVATION_CLASS_NAME = 'shadow-popover';
